import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@dhl-official/tokens/index.css';
import { defineCustomElements } from '@dhl-official/react-library';
import App from './pages/App';
import ThemeProvider from './styles/ThemeContext';
import IconProvider from './assets/IconContext';
import LoadingProvider from './contexts/LoadingContext';
import NotificationProvider from './contexts/NotificationContext';
import LanguageProvider from './contexts/LanguageContext';
import CountryProvider from './contexts/CountryContext';
import { UserProvider } from './contexts/UserContext';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { msalConfig, loginRequest } from './authConfig';
import { PATHS } from './pages/App/constants';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import './utils/grafana';
import { ApplicationBrandingProvider } from './contexts/ApplicationBrandingContext';
import { setupOpenAPI } from './openapi/openapi';
import logger from './utils/logger';
import cookies from 'js-cookie';
import {
  ensureLoginAccessTokenCookie,
  LOGIN_ACCESS_TOKEN_COOKIE,
  LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE
} from './utils/loginAccessTokenCookie';

setupOpenAPI();

export const msalInstance = new PublicClientApplication(msalConfig);

// Eagerly set a presence cookie for returning users to avoid first-load 403 on protected chunks
try {
  const accs = msalInstance.getAllAccounts();
  if (accs.length > 0) {
    cookies.set(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, '1', {
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax'
    });
    logger.auth.debug('loginAccessToken presence cookie set eagerly');
  } else {
    cookies.remove(LOGIN_ACCESS_TOKEN_COOKIE, { path: '/' });
    cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, { path: '/' });
  }
} catch (e) {
  // non-fatal
}

// Attempt to set the loginAccessToken cookie as early as possible so Apache can serve protected JS
const setLoginAccessTokenCookie = async (): Promise<void> => {
  const isSet = await ensureLoginAccessTokenCookie({
    instance: msalInstance,
    loginRequest
  });

  if (!isSet) {
    cookies.remove(LOGIN_ACCESS_TOKEN_COOKIE, { path: '/' });
    cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, { path: '/' });
  }
};

// Flag to ensure the login audit API is called exactly once per page load.
// Needed because LOGIN_SUCCESS fires during handleRedirectPromise(), so both
// the event callback and the .then() handler run for redirect-based logins.
let loginAuditCalled = false;

/**
 * Call GET /rafadmin/user/login to record the login event in the backend DB.
 * Must only be called AFTER the loginAccessToken cookie is confirmed set,
 * otherwise the protected JS chunk import and the authenticated request both fail.
 * The dedup flag prevents double-logging when both the event callback and
 * handleRedirectPromise resolve for the same redirect-based login.
 */
const callLoginAuditApi = async (): Promise<void> => {
  if (loginAuditCalled) {
    logger.auth.debug('Login audit already called this page load, skipping duplicate');
    return;
  }
  loginAuditCalled = true;
  try {
    const { default: protectedServices } = await import('./service/protectedServices');
    await protectedServices.logLogin();
    logger.auth.debug('Login audit API (/rafadmin/user/login) called successfully');
  } catch (err) {
    // Reset flag so a transient failure can be retried on next navigation
    loginAuditCalled = false;
    logger.auth.error('Login logging failed:', err);
  }
};

// Set up event callback for post-authentication actions
// Note: We only handle LOGIN_SUCCESS here to avoid race conditions with handleRedirectPromise
msalInstance.addEventCallback(message => {
  if (message.eventType === EventType.LOGIN_SUCCESS) {
    logger.auth.debug('LOGIN_SUCCESS event received');
    const accs = msalInstance.getAllAccounts();
    if (accs.length > 0) {
      msalInstance.setActiveAccount(accs[0]);
      logger.auth.debug('Active account set on LOGIN_SUCCESS:', accs[0].username);
    }

    // Set cookie after successful login, then call the audit API.
    // IMPORTANT: both must run sequentially — logLogin() requires the
    // loginAccessToken cookie to be present before the HTTP request fires.
    // Previously setLoginAccessTokenCookie() was fire-and-forget, causing a
    // race where logLogin() ran before the cookie was available (403/401).
    void (async () => {
      await setLoginAccessTokenCookie();
      await callLoginAuditApi();
    })();
  }
});

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

library.add(fas);
library.add(far);
library.add(fab);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const APP_SWITCH_QUERY_PARAM = 'mscAppSwitch';

const wasAppSwitchLanding = (): boolean => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(APP_SWITCH_QUERY_PARAM) === '1') {
      url.searchParams.delete(APP_SWITCH_QUERY_PARAM);
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      return true;
    }
  } catch (error) {
    logger.auth.debug('Unable to inspect switcher marker in URL:', error);
  }

  return false;
};

/**
 * Deterministically verify that the loginAccessToken cookie is available to network requests.
 * Uses exponential backoff to retry until cookie propagates to the browser's network layer.
 * This eliminates race conditions from the timing-based approach.
 */
const verifyCookieAvailability = async (): Promise<boolean> => {
  const accs = msalInstance.getAllAccounts();
  if (accs.length === 0) {
    // No accounts, no cookie needed
    logger.auth.debug('No MSAL accounts, skipping cookie verification');
    return true;
  }

  const maxAttempts = 20; // Up to ~10 seconds total
  let attempt = 0;
  let delay = 50; // Start with 50ms

  while (attempt < maxAttempts) {
    attempt++;

    // First check: Is cookie present in JavaScript?
    const cookieValue = cookies.get(LOGIN_ACCESS_TOKEN_COOKIE);
    const presenceCookieValue = cookies.get(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE);
    if (
      (cookieValue === undefined || cookieValue === null || cookieValue === '') &&
      (presenceCookieValue === undefined || presenceCookieValue === null || presenceCookieValue === '')
    ) {
      logger.auth.error('loginAccessToken cookie missing from document.cookie!');
      return false;
    }

    try {
      // Second check: Is cookie available to fetch() network layer?
      // Make a test request to verify cookie propagation
      // We test by fetching a public resource to avoid triggering authentication flows
      const testResponse = await fetch(`${window.location.origin}/favicon.ico`, {
        method: 'HEAD',
        credentials: 'include', // Ensure cookies are sent
        cache: 'no-store' // Don't cache this test
      });

      // If we got a response (any status), the network layer is working
      // This doesn't guarantee the cookie was sent, but indicates network is ready
      if (testResponse !== null && testResponse !== undefined) {
        logger.auth.info(`Cookie verified available after ${attempt} attempts (${(attempt * delay) / 1000}s)`);
        return true;
      }
    } catch (error) {
      logger.auth.debug(`Cookie verification attempt ${attempt} failed:`, error);
    }

    // Exponential backoff: 50ms, 100ms, 200ms, 400ms, 800ms, capped at 1000ms
    await new Promise(resolve => setTimeout(resolve, delay));
    delay = Math.min(delay * 2, 1000);
  }

  // Failed after all attempts
  logger.auth.error(`Cookie verification failed after ${maxAttempts} attempts (~10 seconds)`);
  return false;
};

// Handle redirect promise BEFORE rendering to avoid interaction_in_progress errors
// This ensures MSAL state is fully initialized before the app tries to use it
msalInstance.handleRedirectPromise()
  .then(async (response) => {
    if (response !== null) {
      // Successful redirect return from Azure AD
      logger.auth.debug('Redirect successful, authentication response received');
      const accs = msalInstance.getAllAccounts();
      if (accs.length > 0) {
        msalInstance.setActiveAccount(accs[0]);
        logger.auth.debug('Active account set after redirect:', accs[0].username);
      }

      // CRITICAL: Use the token from the redirect response directly instead of acquireTokenSilent
      // This avoids race conditions where the token cache isn't ready yet after MS login redirect
      // The token must be set BEFORE app renders to prevent 403 errors on protected JS chunks
      const token = response.accessToken ?? '';
      if (token !== '') {
        // Set cookie with SameSite=None for cross-site redirects from Azure AD
        // This ensures the cookie is sent with subsequent fetch() requests for protected resources
        const cookieOptions = {
          path: '/',
          secure: process.env.NODE_ENV === 'production',
          sameSite: process.env.NODE_ENV === 'production' ? 'none' as const : 'lax' as const
        };
        cookies.set(LOGIN_ACCESS_TOKEN_COOKIE, token, cookieOptions);
        cookies.remove(LOGIN_ACCESS_TOKEN_PRESENCE_COOKIE, { path: '/' });
        logger.auth.debug(`loginAccessToken cookie set from redirect response with SameSite=${  cookieOptions.sameSite}`);
      } else {
        // Fallback to acquireTokenSilent if token not in response
        logger.auth.warn('No token in redirect response, falling back to acquireTokenSilent');
        await setLoginAccessTokenCookie();
      }

      // Call the login audit API here — after the cookie is confirmed set —
      // in addition to the LOGIN_SUCCESS event handler path.
      // Reason: LOGIN_SUCCESS fires *during* handleRedirectPromise() (before
      // .then() resolves), so when the event fires the cookie is not yet set
      // and the dynamic import of protectedServices gets a 403 from Apache.
      // Calling it here, after the cookie is ready, guarantees it runs.
      // The loginAuditCalled flag prevents a duplicate call if the event
      // handler also manages to complete first.
      await callLoginAuditApi();
    } else {
      // No redirect in progress - check if we have a returning user
      logger.auth.debug('No redirect in progress, checking for existing session');
      const accs = msalInstance.getAllAccounts();
      if (accs.length > 0) {
        msalInstance.setActiveAccount(accs[0]);
        logger.auth.debug('Active account set for returning user:', accs[0].username);
        await setLoginAccessTokenCookie();
      }	else {
        // No local MSAL accounts (e.g. arriving from a different app like LifeTrack).
        // If the user came here from the in-app switcher, skip the hidden iframe path.
        // Edge blocks that redirect chain in this network setup, so use a top-level redirect instead.
        if (wasAppSwitchLanding()) {
          logger.auth.debug('App switch landing detected, starting loginRedirect');
          await msalInstance.loginRedirect(loginRequest);
        } else {
          // Attempt a silent SSO using the Azure AD session cookie so the user is not
          // re-prompted to log in if they are already authenticated on another app.
          try {
            logger.auth.debug('No local MSAL accounts found, attempting ssoSilent to reuse Azure AD session');
            const ssoResponse = await msalInstance.ssoSilent(loginRequest);
            if (ssoResponse?.account != null) {
              msalInstance.setActiveAccount(ssoResponse.account);
              logger.auth.debug('ssoSilent succeeded, active account set:', ssoResponse.account.username);
              await setLoginAccessTokenCookie();
              // Audit the login — cookie is now confirmed set, same as the redirect path.
              // loginAuditCalled flag prevents duplicate if LOGIN_SUCCESS also fires.
              await callLoginAuditApi();
            }
          } catch (ssoError) {
            // Expected when Azure has no active session or interaction is required.
            // Fall through — the normal interactive login flow will handle it.
            logger.auth.debug('ssoSilent failed (no Azure session available or interaction required):', ssoError);
          }
        }
      }
    }

    // CRITICAL: Verify cookie is available to network layer before rendering
    // This prevents 403 errors on protected chunks by ensuring the cookie
    // has propagated from document.cookie to the browser's fetch() mechanism
    const isVerified = await verifyCookieAvailability();

    if (!isVerified) {
      logger.auth.error('Cookie verification failed - protected resources may not load correctly');
      // Continue anyway to avoid blocking users, but log the issue for monitoring
      // In production, you might want to show an error message or retry
    }
  })
  .catch((error) => {
    // Handle authentication errors gracefully
    // Note: BrowserAuthError with errorCode "no_account_error" is expected when not logged in
    logger.auth.error('Error during redirect handling:', error);
  })
  .finally(() => {
    // Render the app only after MSAL initialization AND cookie verification
    // This prevents race conditions and interaction_in_progress errors
    logger.auth.debug('MSAL initialization complete, rendering app');
    renderApp();
  });

const theme = createTheme({
  typography: { fontFamily: "'Delivery'", fontSize: 13.5 },
  palette: {
    mode: 'light',
    primary: {
      main: '#000000'
    },
    secondary: {
      main: '#f50057'
    }
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          paddingTop: '20px'
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          overflow: 'hidden',
          borderRadius: 4,
          backgroundColor: 'transparent',
          border: '1px solid',
          borderColor: 'rgb(140, 140, 140)',
          transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
          '&:hover': {
            backgroundColor: 'transparent'
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
            borderColor: 'rgb(140, 140, 140)'
          }
        }),
        input: {
          paddingTop: '20px',
          paddingBottom: '3px',
          '&::placeholder': {
            fontSize: '13px'
          }
        }
      },
      defaultProps: {
        disableUnderline: true
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: 'white'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: 0,
          color: '#d40511',
          fontSize: '14px'
        })
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '14px',
          color: 'rgb(102, 102, 102)',
          transform: 'unset !important',
          paddingLeft: '14px',
          top: '0 !important'
        })
      }
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      }
    }
  }
});

function renderApp(): void {
  root.render(
    // This is removed because of all the context APIs getting called twice due to this
    // <React.StrictMode>
    <ThemeProvider>
      <MuiThemeProvider theme={theme}>
        <IconProvider>
          <LoadingProvider>
            <NotificationProvider>
              <LanguageProvider>
                <CountryProvider>
                  <MsalProvider instance={msalInstance}>
                    <UserProvider ignorePath={[PATHS.ERROR, PATHS.INACTIVE, PATHS.EXPIRED, PATHS.NOT_FOUND, PATHS.LOGOUT]}>
                      <ApplicationBrandingProvider>
                        <App />
                      </ApplicationBrandingProvider>
                    </UserProvider>
                  </MsalProvider>
                </CountryProvider>
              </LanguageProvider>
            </NotificationProvider>
          </LoadingProvider>
        </IconProvider>
      </MuiThemeProvider>
    </ThemeProvider>
    // </React.StrictMode>
  );

  // Ensure the defineCustomElements() function is called after ReactDOM.render()
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  defineCustomElements();
}
